import type { TechniqueCard } from "../types";
import { createId } from "../utils/ids";

type VisualKey = "observe" | "balance" | "check" | "calibrate";
type FormulaMode = "character" | "fact" | "question" | "prediction" | "recovery" | "influence";

interface FormulaSeed {
  title: string;
  group: string;
  original: string;
  ethical: string;
  principle: string;
  mode: FormulaMode;
  tags?: string[];
}

const visuals: Record<VisualKey, TechniqueCard["visualCue"]> = {
  observe: {
    imageUrl: "/visuals/cold-reading-observe-loop.png",
    imageAlt: "두 사람이 대화하며 작은 단서를 부드러운 질문으로 바꾸는 그림",
    quickInstruction: "작은 단서 하나를 고르고, 낮은 확신도의 가설로 말한 뒤 바로 확인 질문을 붙인다.",
    generationPrompt: "ethical cold reading observation loop, visible cues become gentle checking question, calm conversation"
  },
  balance: {
    imageUrl: "/visuals/cold-reading-balanced-reflection.png",
    imageAlt: "한 사람의 두 감정 면이 겹쳐지고 상대가 차분히 반영하는 그림",
    quickInstruction: "상대의 선택 뒤에 함께 있는 두 욕구를 한 문장에 나란히 놓고 어느 쪽이 더 맞는지 묻는다.",
    generationPrompt: "balanced reflection visual cue, two translucent emotional facets, ethical rapport conversation"
  },
  check: {
    imageUrl: "/visuals/cold-reading-checking-question.png",
    imageAlt: "두 갈래 선택 말풍선 앞에서 상대가 편안하게 선택하는 그림",
    quickInstruction: "맞힌 척하지 말고 가능한 방향을 2-3개로 제시한 뒤 상대가 고르게 한다.",
    generationPrompt: "consent based checking question visual, two or three gentle paths, collaborative conversation"
  },
  calibrate: {
    imageUrl: "/visuals/cold-reading-feedback-calibration.png",
    imageAlt: "대화 피드백을 받고 조정 다이얼을 차분히 맞추는 그림",
    quickInstruction: "틀리면 즉시 인정하고, 상대가 정정한 단어를 다음 기준선으로 삼는다.",
    generationPrompt: "feedback calibration visual cue, compass dial adjusted after response, honest conversation repair"
  }
};

const modeConfig: Record<FormulaMode, {
  visual: VisualKey;
  setup: string[];
  steps: string[];
  prompts: string[];
  successCriteria: string[];
  sentenceTemplates: string[];
  warnings: string[];
}> = {
  character: {
    visual: "balance",
    setup: ["상대의 말을 먼저 듣는다", "성격 단정 대신 양면 욕구를 찾는다", "맞고 틀림을 상대가 고칠 수 있게 둔다"],
    steps: ["원형 공식의 의도를 확인한다", "평가나 아부를 균형 반영으로 바꾼다", "낮은 확신도의 가설로 말한다", "어느 쪽이 더 맞는지 묻는다"],
    prompts: ["두 면", "균형 저울", "수정 가능한 가설"],
    successCriteria: ["성격을 확정하지 않았다", "상대가 정정할 공간을 남겼다", "칭찬으로 의존감을 만들지 않았다"],
    sentenceTemplates: [
      "한쪽으로는 [A]를 원하지만, 동시에 [B]도 지키고 싶은 마음이 있어 보여요. 어느 쪽이 더 가까워요?",
      "제가 단정할 수는 없지만, 지금은 [A]와 [B]가 같이 움직이는 장면처럼 들립니다.",
      "아니라면 바로 고쳐 주세요. 당신에게 더 정확한 표현은 무엇인가요?"
    ],
    warnings: ["누구에게나 맞는 말을 비밀을 맞힌 것처럼 말하지 않는다", "우월감이나 의존감을 유도하지 않는다", "영적 권위로 포장하지 않는다"]
  },
  fact: {
    visual: "observe",
    setup: ["관찰 가능한 단서와 추측을 구분한다", "통계적 가능성을 확실한 사실처럼 말하지 않는다", "민감정보는 묻기 전에 동의를 확인한다"],
    steps: ["넓은 추측을 멈춘다", "보이는 단서나 상대 발화에 근거를 둔다", "가설을 질문으로 바꾼다", "틀리면 즉시 보정한다"],
    prompts: ["작은 단서", "관찰 근거", "확인 질문"],
    successCriteria: ["추측과 사실을 분리했다", "맞힌 척하지 않았다", "상대가 거절할 수 있게 했다"],
    sentenceTemplates: [
      "제가 보기에는 [단서] 때문에 [가설]일 수도 있어 보여요. 맞나요, 아니면 다른 쪽인가요?",
      "이건 확정이 아니라 확인입니다. [A/B/기타] 중 어디에 더 가까워요?",
      "틀렸다면 바로 수정하겠습니다. 어떤 맥락이 더 정확한가요?"
    ],
    warnings: ["건강, 가족, 돈 문제를 단정하지 않는다", "상대가 기억을 끼워 맞추도록 압박하지 않는다", "모호한 말을 예언처럼 말하지 않는다"]
  },
  question: {
    visual: "check",
    setup: ["질문 의도를 숨기지 않는다", "A/B/기타 선택지를 준비한다", "상대가 답하지 않을 권리를 둔다"],
    steps: ["진술처럼 포장된 질문을 공개 질문으로 바꾼다", "가능한 방향을 2-3개로 제시한다", "상대 답변을 반복해 확인한다", "민감 주제는 멈출 수 있게 한다"],
    prompts: ["선택 가능한 말풍선", "열린 문", "수정권"],
    successCriteria: ["질문이라는 사실을 숨기지 않았다", "예/아니오를 왜곡하지 않았다", "상대 답변을 다음 기준으로 삼았다"],
    sentenceTemplates: [
      "제가 단정하지 않고 확인하고 싶습니다. [A]와 [B] 중 어느 쪽이 더 맞나요?",
      "이 질문은 넘어가도 됩니다. 답해도 괜찮다면 [주제]부터 확인해도 될까요?",
      "방금 말씀은 [요약]으로 이해했습니다. 맞게 들었나요?"
    ],
    warnings: ["부정문 덫을 쓰지 않는다", "전문 용어로 상대를 압도하지 않는다", "이미 얻은 정보를 신비한 통찰처럼 재사용하지 않는다"]
  },
  prediction: {
    visual: "calibrate",
    setup: ["미래 단정을 멈춘다", "상대의 바람과 현실 선택지를 분리한다", "검증 가능한 행동 단위로 바꾼다"],
    steps: ["예측 문장을 현재 선택 질문으로 바꾼다", "불확실성을 명시한다", "상대가 통제할 수 있는 다음 행동을 고른다", "결과를 사후에 과장하지 않는다"],
    prompts: ["안개 낀 길", "현재 선택지", "기록 가능한 다음 행동"],
    successCriteria: ["미래를 확정하지 않았다", "희망 고문을 하지 않았다", "상대가 실행 가능한 행동을 정했다"],
    sentenceTemplates: [
      "미래를 단정할 수는 없습니다. 대신 지금 [목표]에 가까워지는 다음 행동을 정리해 볼까요?",
      "[가능성]은 있을 수 있지만 확정은 아닙니다. 확인 가능한 작은 단계부터 보겠습니다.",
      "예측보다 기록이 낫습니다. 오늘 선택할 수 있는 행동 하나는 무엇인가요?"
    ],
    warnings: ["돈, 건강, 사랑의 결과를 확언하지 않는다", "사후확증편향을 이용하지 않는다", "거짓 과거 예측을 주장하지 않는다"]
  },
  recovery: {
    visual: "calibrate",
    setup: ["틀릴 수 있음을 받아들인다", "변명 문장을 내려놓는다", "상대가 정정한 표현을 기준으로 삼는다"],
    steps: ["틀린 부분을 짧게 인정한다", "상대의 정정을 반복한다", "수정된 기준으로 다시 묻는다", "신뢰가 흔들리면 멈출 선택지를 준다"],
    prompts: ["조정 다이얼", "되돌아오는 화살표", "새 기준선"],
    successCriteria: ["기억 탓이나 상징 해석으로 회피하지 않았다", "정정을 다음 문장에 반영했다", "대화 중단 선택지를 존중했다"],
    sentenceTemplates: [
      "그건 제가 잘못 짚었습니다. 방금 말씀해 주신 기준으로 다시 보겠습니다.",
      "제가 앞서갔네요. 핵심은 [상대가 정정한 내용] 쪽이군요.",
      "그 부분은 제 추측이었습니다. 알려주신 범위 안에서만 이어가겠습니다."
    ],
    warnings: ["상대 기억력 탓으로 돌리지 않는다", "미래에 맞을 것이라고 미루지 않는다", "상징이나 큰 틀로 바꿔 우기지 않는다"]
  },
  influence: {
    visual: "balance",
    setup: ["권위나 의존을 만들려는 표현을 제거한다", "상대의 선택권을 먼저 둔다", "감정 반영과 제안을 분리한다"],
    steps: ["상대의 현재 감정을 반영한다", "특별함이나 보상을 확언하지 않는다", "선택 가능한 도움을 제안한다", "거절 가능성을 명확히 둔다"],
    prompts: ["열린 손", "선택권", "낮은 확신도"],
    successCriteria: ["상대 선택권을 보존했다", "강한 권위나 암시를 쓰지 않았다", "보상이나 구원을 약속하지 않았다"],
    sentenceTemplates: [
      "지금 [감정/욕구]가 크게 느껴질 수는 있어요. 다만 선택은 당신에게 있습니다.",
      "제가 볼 수 있는 건 [관찰] 정도입니다. 도움이 된다면 다음 선택지를 같이 정리해 볼 수 있어요.",
      "이 제안은 거절해도 괜찮습니다. 지금 필요한 방식으로 조정하겠습니다."
    ],
    warnings: ["무의식 주입, 의존 유도, 보상 약속을 사용하지 않는다", "상대의 두려움이나 결핍을 이용하지 않는다", "신비한 권위로 복종을 요구하지 않는다"]
  }
};

const formulaSeeds: FormulaSeed[] = [
  {
    title: "무지개 화법 (The Rainbow Ruse)",
    group: "성격 및 성향 분석",
    original: "당신은 주로 [A 성향]이지만, 때로는 [A의 정반대 성향]을 보이기도 합니다.",
    ethical: "당신 안에 [A 욕구]와 [반대 욕구]가 같이 있을 수 있어 보여요. 어느 쪽이 더 강한가요?",
    principle: "양면성을 단정형 성격 판정이 아니라 수정 가능한 갈등 구조로 반영한다.",
    mode: "character",
    tags: ["양면성", "성격 반영"]
  },
  {
    title: "정교한 아부 (Fine Flattery)",
    group: "성격 및 성향 분석",
    original: "대부분의 사람들은 [부정적]이지만, 당신은 그들보다 조금 더 [긍정적]인 사람입니다.",
    ethical: "당신이 중요하게 지키려는 [긍정 기준]이 있는 것 같아요. 이 기준이 지금 상황과 관련 있나요?",
    principle: "비교 우월감을 주입하지 않고 상대가 중요하게 여기는 기준을 확인한다.",
    mode: "character",
    tags: ["칭찬 전환", "기준 확인"]
  },
  {
    title: "영매의 칭찬 (The Psychic Credit)",
    group: "성격 및 성향 분석",
    original: "당신에게는 남들보다 뛰어난 직관력 또는 영적 감각이 있습니다.",
    ethical: "당신이 미묘한 신호를 잘 noticing 하는 편인지 확인하고 싶어요. 그런 경험이 있나요?",
    principle: "초자연 권위를 부여하지 않고 관찰 민감도나 자기 인식으로 낮춰 확인한다.",
    mode: "character",
    tags: ["직관", "권위 제거"]
  },
  {
    title: "설탕 덩어리 (Sugar Lumps)",
    group: "성격 및 성향 분석",
    original: "마음을 열고 조언을 받아들이면 [큰 보상/사랑]을 얻게 될 것입니다.",
    ethical: "이 조언이 맞는지 스스로 검토해 보고, 도움이 되는 부분만 가져가도 됩니다.",
    principle: "보상 약속으로 비판성을 낮추지 않고 선택권과 검토권을 돌려준다.",
    mode: "influence",
    tags: ["선택권", "보상 약속 금지"]
  },
  {
    title: "자크의 진술 (The Jacques Statement)",
    group: "성격 및 성향 분석",
    original: "어릴 적 꿈과 재능을 현실과 타협하느라 미뤄두었지만, 여전히 갈망하고 있군요.",
    ethical: "예전에 중요했지만 지금은 뒤로 밀린 관심사가 있는지 궁금합니다. 아직 의미가 남아 있나요?",
    principle: "보편적 인생 단계 진술을 개인 통찰처럼 포장하지 않고 열린 회상 질문으로 바꾼다.",
    mode: "character",
    tags: ["인생 단계", "회상 질문"]
  },
  {
    title: "남의 떡 찌르기 (Greener Grass)",
    group: "성격 및 성향 분석",
    original: "현재 삶에 충실하지만, 때로는 선택하지 않았던 다른 삶의 방식에 미련을 가집니다.",
    ethical: "지금 선택을 유지하고 싶은 마음과 다른 가능성을 궁금해하는 마음이 같이 있나요?",
    principle: "가지 않은 길에 대한 보편적 미련을 단정하지 않고 선택 갈등으로 확인한다.",
    mode: "character",
    tags: ["선택 갈등", "가지 않은 길"]
  },
  {
    title: "바넘 진술 (Barnum Statements)",
    group: "성격 및 성향 분석",
    original: "타인에게 인정받고 싶은 욕구가 강하며, 가끔 자신의 결정에 의구심을 가집니다.",
    ethical: "인정 욕구나 결정 의심이 이번 상황에서 실제로 영향을 주고 있나요?",
    principle: "누구에게나 맞는 문장을 개인 진단처럼 쓰지 않고 현재 상황과의 관련성을 확인한다.",
    mode: "character",
    tags: ["바넘", "포러 효과"]
  },
  {
    title: "모호한 사실 (The Fuzzy Fact)",
    group: "사실 및 과거 사건 도출",
    original: "[지리/건강/직업 관련 매우 넓고 모호한 사실]과 관련된 에너지가 보입니다.",
    ethical: "[주제]와 관련이 있을 수도 있지만 확실하지 않습니다. 떠오르는 맥락이 있나요?",
    principle: "모호한 사실을 맞힌 척하지 않고 상대가 의미를 붙일지 선택하게 한다.",
    mode: "fact",
    tags: ["모호성", "확인"]
  },
  {
    title: "확률 높은 추측 (The Good Chance Guess)",
    group: "사실 및 과거 사건 도출",
    original: "집 주소에 숫자 2가 들어가나요? 파란색 차가 연관되어 있군요.",
    ethical: "흔한 가능성이라 조심스럽게 묻겠습니다. [가설]이 관련 있나요, 아니면 넘어갈까요?",
    principle: "통계적으로 흔한 추측을 확정 사실처럼 말하지 않는다.",
    mode: "fact",
    tags: ["확률", "흔한 단서"]
  },
  {
    title: "행운의 추측 (The Lucky Guess)",
    group: "사실 및 과거 사건 도출",
    original: "알파벳 J로 시작하는 사람이나 8월 말 즈음의 날짜가 느껴집니다.",
    ethical: "무작위 추측은 빼고, 지금 대화에서 나온 단서만 기준으로 확인하겠습니다.",
    principle: "우연한 적중을 유도하지 않고 대화 근거가 있는 질문으로 제한한다.",
    mode: "fact",
    tags: ["무작위 추측", "근거 제한"]
  },
  {
    title: "통계적 사실 (The Stat Fact)",
    group: "사실 및 과거 사건 도출",
    original: "별자리는 당신이 [특정 산업/분야]에서 사람들을 돕는 일을 하도록 이끕니다.",
    ethical: "일반 통계나 배경 정보로 보면 [가능성]이 있을 수 있습니다. 본인 경험과 맞나요?",
    principle: "통계나 배경 지식을 신비한 통찰로 포장하지 않고 출처 성격을 분리한다.",
    mode: "fact",
    tags: ["통계", "출처 분리"]
  },
  {
    title: "사소한 통계 (The Trivia Stat)",
    group: "사실 및 과거 사건 도출",
    original: "집에는 짝 잃은 귀걸이, 고장 난 시계, 정리되지 않은 사진 상자가 있군요.",
    ethical: "대부분의 사람이 가진 사소한 흔적일 수 있습니다. 이 주제는 지금 대화에 중요하지 않으면 넘기겠습니다.",
    principle: "흔한 사물 추측을 개인 비밀 적중처럼 다루지 않는다.",
    mode: "fact",
    tags: ["생활 단서", "흔한 패턴"]
  },
  {
    title: "문화적 트렌드 (The Cultural Trend)",
    group: "사실 및 과거 사건 도출",
    original: "최근 [건강/운동/재테크 등 사회적 유행]에 큰 관심을 쏟고 있습니다.",
    ethical: "요즘 많은 사람이 [트렌드]를 신경 쓰는데, 당신에게도 관련이 있나요?",
    principle: "사회적 유행을 개인 통찰처럼 말하지 않고 공통 맥락으로 제시한다.",
    mode: "fact",
    tags: ["문화", "트렌드"]
  },
  {
    title: "어린 시절의 기억 (The Childhood Memory)",
    group: "사실 및 과거 사건 도출",
    original: "어릴 때 크게 다칠 뻔했거나 재능을 보였지만 포기한 관심사가 있었군요.",
    ethical: "어릴 때 기억 중 지금의 선택과 연결되는 일이 있다면 이야기해도 됩니다.",
    principle: "보편적 성장 경험을 단정하지 않고 현재 의미가 있을 때만 묻는다.",
    mode: "fact",
    tags: ["어린 시절", "회상"]
  },
  {
    title: "민속적 지혜 (Folk Wisdom)",
    group: "사실 및 과거 사건 도출",
    original: "어두운 터널 끝에는 결국 빛이 있는 법입니다. 걱정할 필요 없습니다.",
    ethical: "가벼운 위로보다 지금 걱정을 줄이는 구체적 다음 행동을 같이 정리하겠습니다.",
    principle: "상투적 격언으로 불안을 덮지 않고 실행 가능한 지원으로 바꾼다.",
    mode: "prediction",
    tags: ["격언", "위로 전환"]
  },
  {
    title: "계절적 터치 (The Seasonal Touch)",
    group: "사실 및 과거 사건 도출",
    original: "최근 [명절/연말/세일 기간]을 겪으며 인간관계나 금전 피로감을 느꼈군요.",
    ethical: "이 시기에는 [피로]가 흔합니다. 당신에게도 실제로 영향을 줬나요?",
    principle: "달력상의 흔한 스트레스를 개인 내면 적중으로 과장하지 않는다.",
    mode: "fact",
    tags: ["계절", "시기"]
  },
  {
    title: "반대 게임 (The Opposites Game)",
    group: "사실 및 과거 사건 도출",
    original: "당신과 완전히 정반대 성향의 사람이 주변에서 괴롭히고 있군요.",
    ethical: "당신과 기준이 다른 사람과의 마찰이 있다면, 어떤 차이가 가장 큰가요?",
    principle: "가상의 반대 인물을 던져 끼워 맞추게 하지 않고 실제 갈등 기준을 확인한다.",
    mode: "fact",
    tags: ["갈등", "반대 성향"]
  },
  {
    title: "밀어붙이기 진술 (The Push Statement)",
    group: "사실 및 과거 사건 도출",
    original: "상대가 부정해도 분명히 [특이한 사건]과 관련된 일이 있었습니다.",
    ethical: "아니라면 제 추측은 내려놓겠습니다. 다른 단서가 있을 때만 다시 확인하겠습니다.",
    principle: "부정당한 추측을 밀어붙이지 않고 멈추는 능력을 훈련한다.",
    mode: "recovery",
    tags: ["압박 금지", "중단"]
  },
  {
    title: "직접적 질문 (The Direct Question)",
    group: "교묘하게 정보 캐내기",
    original: "지금 당신의 마음을 가장 무겁게 짓누르는 고민은 무엇인가요?",
    ethical: "답해도 괜찮다면, 지금 가장 다루고 싶은 주제가 무엇인지 직접 물어봐도 될까요?",
    principle: "직접 질문은 숨기지 않고 동의를 얻어 사용한다.",
    mode: "question",
    tags: ["직접 질문", "동의"]
  },
  {
    title: "부수적 질문 (The Incidental Question)",
    group: "교묘하게 정보 캐내기",
    original: "[A]라는 느낌이 강하게 드는데, 이것이 당신에게 어떤 의미로 다가오나요?",
    ethical: "[A]라는 단어가 떠오르는데, 당신에게 의미가 없다면 바로 넘기겠습니다.",
    principle: "가벼운 질문도 정보 수집임을 숨기지 않고 거절권을 둔다.",
    mode: "question",
    tags: ["부수 질문", "의미 확인"]
  },
  {
    title: "가려진 질문 (The Veiled Question)",
    group: "교묘하게 정보 캐내기",
    original: "출장이 잦은 업무와 관련된 에너지가 느껴지는데, 제 느낌이 맞나요?",
    ethical: "업무 이동이나 일정 변화가 관련 있는지 확인해도 될까요?",
    principle: "진술처럼 포장된 질문을 명시적 확인 질문으로 바꾼다.",
    mode: "question",
    tags: ["가려진 질문", "명시화"]
  },
  {
    title: "우회적 질문 (The Diverted Question)",
    group: "교묘하게 정보 캐내기",
    original: "앞서 알아낸 직업 정보를 나중에 건강이나 스트레스 해석에 재사용합니다.",
    ethical: "앞서 말한 [정보]를 바탕으로 추정하면 [가설]일 수 있습니다. 연결해도 괜찮나요?",
    principle: "이미 얻은 정보를 신비한 통찰처럼 재사용하지 않고 근거를 밝힌다.",
    mode: "question",
    tags: ["정보 재사용", "근거 표시"]
  },
  {
    title: "전문 용어 폭격 (The Jargon Blitz)",
    group: "교묘하게 정보 캐내기",
    original: "뜻 모를 전문 용어를 말한 뒤 고립을 의미하는데 맞나요?",
    ethical: "전문 용어 없이 말하면, 지금은 [의미]에 가까운지 확인하고 싶습니다.",
    principle: "용어로 권위를 만들지 않고 쉬운 말로 의미를 확인한다.",
    mode: "question",
    tags: ["전문 용어", "쉬운 말"]
  },
  {
    title: "사라지는 부정문 (The Vanishing Negative)",
    group: "교묘하게 정보 캐내기",
    original: "아이들과 관련된 일은 안 하시죠? 어떤 답이든 맞힌 것처럼 전환합니다.",
    ethical: "[A]와 관련이 있는지 없는지 확인하고 싶습니다. 어느 쪽이든 그대로 받아들이겠습니다.",
    principle: "부정문 덫을 쓰지 않고 예/아니오를 있는 그대로 기록한다.",
    mode: "question",
    tags: ["부정문", "정직한 확인"]
  },
  {
    title: "셜록 전략 (The Sherlock Strategy)",
    group: "교묘하게 정보 캐내기",
    original: "굳은살, 흉터, 옷차림 등을 관찰한 후 취미나 직업을 추론합니다.",
    ethical: "[관찰한 단서] 때문에 [가설]일 수도 있다고 생각했습니다. 맞는지 확인해도 될까요?",
    principle: "관찰 추론은 근거와 불확실성을 함께 말한다.",
    mode: "fact",
    tags: ["관찰", "추론"]
  },
  {
    title: "러시아 인형 기법 (The Russian Doll)",
    group: "교묘하게 정보 캐내기",
    original: "딸이 아니면 며느리, 아니면 딸처럼 아끼는 아이로 의미를 계속 넓힙니다.",
    ethical: "처음 가설이 아니라면 범위를 억지로 넓히지 않고, 상대가 말한 범위 안에서 다시 묻겠습니다.",
    principle: "맞을 때까지 의미를 확장하지 않고 틀린 가설을 내려놓는다.",
    mode: "recovery",
    tags: ["범위 확장", "보정"]
  },
  {
    title: "눈동자 접근 단서 (Eye Accessing Cues)",
    group: "교묘하게 정보 캐내기",
    original: "눈동자 방향으로 과거 이미지나 소리를 떠올리는지 읽습니다.",
    ethical: "표정이나 시선만으로 단정하지 않겠습니다. 지금 떠오르는 것이 있다면 직접 말해 주세요.",
    principle: "비검증 단서를 마음 읽기처럼 쓰지 않고 자기 보고를 요청한다.",
    mode: "question",
    tags: ["NLP", "단정 금지"]
  },
  {
    title: "피터팬 예측 (Peter Pan Predictions)",
    group: "미래 예측",
    original: "가장 걱정하는 돈, 건강, 사랑 문제는 결국 원만하게 해결될 것입니다.",
    ethical: "해결을 장담할 수는 없지만, 지금 부담을 줄일 작은 행동을 정해 볼 수 있습니다.",
    principle: "간절한 희망을 예측처럼 주입하지 않고 현재 행동으로 전환한다.",
    mode: "prediction",
    tags: ["희망", "현재 행동"]
  },
  {
    title: "폴리아나의 진주 (Pollyanna Pearls)",
    group: "미래 예측",
    original: "지난 18개월간 힘들었지만 다음 달부터 훨씬 나아질 것입니다.",
    ethical: "최근 힘든 기간이 있었다면 인정하겠습니다. 다만 회복 시점은 단정하지 않고 준비할 행동을 보겠습니다.",
    principle: "구체적인 기간의 낙관 예측을 위로와 계획으로 분리한다.",
    mode: "prediction",
    tags: ["낙관", "기간 단정 금지"]
  },
  {
    title: "확실한 예측 (Certain Predictions)",
    group: "미래 예측",
    original: "새로운 사람이 들어오거나 돈 문제 갈등이 생길 것입니다.",
    ethical: "언젠가 생길 수 있는 일을 예측처럼 말하지 않고, 대비가 필요한 영역을 점검하겠습니다.",
    principle: "거의 반드시 일어날 일반 사건을 예언처럼 다루지 않는다.",
    mode: "prediction",
    tags: ["일반 사건", "대비"]
  },
  {
    title: "50 대 50 예측 (50/50 Predictions)",
    group: "미래 예측",
    original: "시험에 합격할 것입니다. 그 사람과 결혼하게 될 것입니다.",
    ethical: "결과를 찍지 않고, 성공 가능성을 높이는 변수와 다음 행동을 확인하겠습니다.",
    principle: "양자택일 결과를 자신감 있게 찍는 대신 준비 요인을 점검한다.",
    mode: "prediction",
    tags: ["양자택일", "준비 변수"]
  },
  {
    title: "그럴싸한 예측 (Likely Predictions)",
    group: "미래 예측",
    original: "다음 달 안에 오랫동안 연락이 끊겼던 지인에게 연락이 올 것입니다.",
    ethical: "일어날 법한 가능성은 가능성으로만 두고, 연락을 원한다면 먼저 할 행동을 정하겠습니다.",
    principle: "그럴듯한 사건에 시한을 붙여 확정하지 않는다.",
    mode: "prediction",
    tags: ["가능성", "타임라인"]
  },
  {
    title: "일어날 법하지 않은 예측 (Unlikely Predictions)",
    group: "미래 예측",
    original: "4주 안에 특이한 조건의 옛 친구를 보게 될 것입니다.",
    ethical: "희귀한 우연을 예측하지 않고, 실제 확인 가능한 관심사나 연락 계획으로 바꾸겠습니다.",
    principle: "낮은 확률의 우연을 PR 효과로 이용하지 않는다.",
    mode: "prediction",
    tags: ["희귀 예측", "우연"]
  },
  {
    title: "사실적 예측 (Factual Predictions)",
    group: "미래 예측",
    original: "내년 3월경에 긴 여행을 떠나게 됩니다.",
    ethical: "여행 가능성은 계획과 조건을 확인해야 합니다. 실제 일정이나 욕구가 있는지 보겠습니다.",
    principle: "미래 사실을 찍지 않고 일정, 조건, 욕구를 확인한다.",
    mode: "prediction",
    tags: ["미래 사실", "조건 확인"]
  },
  {
    title: "자기 충족적 예측 (Self-fulfilling Predictions)",
    group: "미래 예측",
    original: "과거 상처를 털고 긍정적인 태도로 새로운 친구를 사귀게 될 것입니다.",
    ethical: "원한다면 관계를 넓히는 행동 계획을 세울 수 있습니다. 지금 가능한 첫 행동은 무엇인가요?",
    principle: "예측이 행동을 유도한다는 점을 숨기지 않고 목표 설정으로 전환한다.",
    mode: "prediction",
    tags: ["행동 유도", "목표 설정"]
  },
  {
    title: "모호한 예측 (Vague Predictions)",
    group: "미래 예측",
    original: "삶은 곧 새로운 진전의 단계에 접어들 것입니다.",
    ethical: "새로운 진전이 무엇을 뜻하는지 구체화해 보겠습니다. 어떤 변화가 필요하다고 느끼나요?",
    principle: "어디에나 붙는 예측을 구체적 목표 질문으로 바꾼다.",
    mode: "prediction",
    tags: ["모호성", "목표 구체화"]
  },
  {
    title: "검증 불가능한 예측 (Unverifiable Predictions)",
    group: "미래 예측",
    original: "직장에서 당신을 질투하는 사람이 있지만 모르는 사이에 이겨낼 것입니다.",
    ethical: "검증할 수 없는 적대자는 단정하지 않겠습니다. 실제 관찰된 행동만 기준으로 보겠습니다.",
    principle: "확인 불가능한 적대 서사를 만들지 않고 관찰 가능한 사실로 제한한다.",
    mode: "prediction",
    tags: ["검증 불가", "사실 기준"]
  },
  {
    title: "일방향 검증 예측 (One-way Verifiable Predictions)",
    group: "미래 예측",
    original: "누군가 중요한 제안을 하려다 마지막 순간에 마음을 바꿀 수 있습니다.",
    ethical: "확인되지 않는 가능성은 가능성으로만 두고, 실제 제안이 오면 그때 판단하겠습니다.",
    principle: "맞으면 맞고 안 맞아도 빠져나가는 예측 구조를 쓰지 않는다.",
    mode: "prediction",
    tags: ["일방향 검증", "가능성 제한"]
  },
  {
    title: "한 번도 없었던 예측 (The Neverwas Prediction)",
    group: "미래 예측",
    original: "제가 5년 전에 이 사건을 정확히 예측했었습니다.",
    ethical: "기록된 근거가 없으면 과거에 예측했다고 말하지 않겠습니다.",
    principle: "거짓 과거 예측이나 명성 조작을 금지하고 기록 근거만 말한다.",
    mode: "prediction",
    tags: ["거짓 예측", "기록 근거"]
  },
  {
    title: "지속하고 뜸들이기 (Persist, wonder and let it linger)",
    group: "무오류 방어 전략",
    original: "다시 잘 생각해 보세요. 분명히 연관된 사람이 있을 겁니다.",
    ethical: "바로 떠오르지 않으면 제 추측은 내려놓겠습니다.",
    principle: "상대가 기억을 억지로 찾게 만들지 않고 틀린 추측을 종료한다.",
    mode: "recovery",
    tags: ["지속 압박 금지", "종료"]
  },
  {
    title: "당신의 기억력 탓 (I am right, but you have forgotten)",
    group: "무오류 방어 전략",
    original: "너무 어릴 적 일이라 당신이 기억을 못 하는 겁니다.",
    ethical: "기억나지 않는다면 확인할 수 없는 정보로 남기고 넘어가겠습니다.",
    principle: "상대 기억을 깎아내리지 않고 검증 불가로 처리한다.",
    mode: "recovery",
    tags: ["기억 탓 금지", "검증 불가"]
  },
  {
    title: "당신이 모를 뿐 (I am right, but you do not know)",
    group: "무오류 방어 전략",
    original: "당신 쪽 인맥이 아니라서 모르는 사람입니다.",
    ethical: "본인이 모르는 정보라면 제가 확인할 수 없으니 주장하지 않겠습니다.",
    principle: "모르는 영역으로 회피하지 않고 증거 없는 주장을 멈춘다.",
    mode: "recovery",
    tags: ["모를 뿐 금지", "증거"]
  },
  {
    title: "아무도 모를 뿐 (I am right, but nobody knows)",
    group: "무오류 방어 전략",
    original: "아직 본인도 깨닫지 못했을 뿐입니다.",
    ethical: "아무도 확인하지 못한 일은 단정하지 않겠습니다.",
    principle: "미확인 사실을 숨은 진실로 포장하지 않는다.",
    mode: "recovery",
    tags: ["숨은 진실 금지", "미확인"]
  },
  {
    title: "부끄러운 비밀 (I am right, but it's embarrassing)",
    group: "무오류 방어 전략",
    original: "집안에서 쉬쉬하고 숨긴 겁니다.",
    ethical: "민감하거나 부끄러운 주제일 수 있으니, 당신이 꺼내지 않으면 묻지 않겠습니다.",
    principle: "수치심을 이용해 부정을 막지 않고 사생활 경계를 존중한다.",
    mode: "recovery",
    tags: ["수치심 금지", "사생활"]
  },
  {
    title: "미래에 일어날 일 (I am wrong now, but I will be right soon)",
    group: "무오류 방어 전략",
    original: "아직 일어나지 않았네요. 곧 조만간 발생할 일입니다.",
    ethical: "지금 맞지 않으면 틀린 추측으로 두겠습니다. 미래로 미루지 않겠습니다.",
    principle: "틀린 주장을 미래로 이동시켜 회피하지 않는다.",
    mode: "recovery",
    tags: ["미래 회피 금지", "오류 인정"]
  },
  {
    title: "사소한 오류 취급 (I am wrong, but it doesn't matter)",
    group: "무오류 방어 전략",
    original: "숫자는 틀렸지만 집 자체의 기운이 중요한 겁니다.",
    ethical: "세부가 틀렸다면 그 추측은 신뢰하지 않겠습니다.",
    principle: "틀린 디테일을 사소하게 축소하지 않고 신뢰도에 반영한다.",
    mode: "recovery",
    tags: ["오류 축소 금지", "신뢰도"]
  },
  {
    title: "감정적 진실 (I am wrong in fact, but right emotionally)",
    group: "무오류 방어 전략",
    original: "사실은 틀렸지만 내면으로는 원했군요.",
    ethical: "사실이 틀렸다면 감정 해석도 다시 확인하겠습니다.",
    principle: "사실 오류를 감정 해석으로 덮지 않는다.",
    mode: "recovery",
    tags: ["감정 덮기 금지", "재확인"]
  },
  {
    title: "시스템의 상징성 (I am wrong in fact, but right within system)",
    group: "무오류 방어 전략",
    original: "시스템에서는 자매가 친동생이 아니라 절친한 여성 지인을 상징합니다.",
    ethical: "상징 해석으로 사실 오류를 바꾸지 않고, 처음 의미가 틀렸음을 인정하겠습니다.",
    principle: "상징 체계로 빠져나가지 않고 합의된 의미를 지킨다.",
    mode: "recovery",
    tags: ["상징 회피 금지", "의미 합의"]
  },
  {
    title: "큰 틀에서 맞음 (Wrong small print, right headline)",
    group: "무오류 방어 전략",
    original: "바퀴가 아니라 브레이크 문제라도 어쨌든 차 문제는 맞죠.",
    ethical: "큰 주제만 맞고 세부가 틀렸다면, 세부는 별도로 정정하겠습니다.",
    principle: "넓은 범주로 맞힌 척하지 않고 세부 정확도를 분리한다.",
    mode: "recovery",
    tags: ["큰 틀 회피 금지", "세부 정정"]
  },
  {
    title: "사과하고 넘어가기 (Accept, apologise, and move on)",
    group: "무오류 방어 전략",
    original: "네, 제가 틀렸네요. 그럼 다음 주제로 넘어가 볼까요?",
    ethical: "제가 틀렸습니다. 이어가도 괜찮다면 새 기준으로 다시 확인하겠습니다.",
    principle: "사과를 회피용으로 쓰지 않고 상대 동의를 얻어 재개한다.",
    mode: "recovery",
    tags: ["사과", "재개 동의"]
  },
  {
    title: "수호천사 선언 (The Angel Statement)",
    group: "무의식 유도 및 심리 통제",
    original: "현재 고통받고 있지만 고유한 능력으로 위대한 성취와 보상을 얻게 될 것입니다.",
    ethical: "지금 힘든 점과 강점 후보를 분리해 보겠습니다. 강점은 가능성으로만 다루겠습니다.",
    principle: "두려움과 특별함, 보상을 묶어 의존을 만들지 않고 강점을 검토 가능한 가능성으로 낮춘다.",
    mode: "influence",
    tags: ["특별함", "의존 금지"]
  },
  {
    title: "3인칭 마법 (Third Person Magic)",
    group: "무의식 유도 및 심리 통제",
    original: "이 사람은 과거에 이런 아픔을 겪었고 장차 잠재력을 발휘할 것입니다.",
    ethical: "당신을 대상화하지 않고 직접 묻겠습니다. 과거 경험과 잠재력 이야기를 해도 괜찮나요?",
    principle: "3인칭 거리감으로 방어를 우회하지 않고 직접 동의를 구한다.",
    mode: "influence",
    tags: ["3인칭", "동의"]
  },
  {
    title: "페이싱과 리딩 (Pacing and Leading)",
    group: "무의식 유도 및 심리 통제",
    original: "확인 가능한 사실 3가지와 그리고를 연결해 원하는 감정이나 생각으로 이끕니다.",
    ethical: "확인 가능한 사실은 사실로만 말하고, 제안은 제안으로 분리하겠습니다.",
    principle: "사실 확인과 행동 유도를 인과처럼 묶지 않고 투명하게 분리한다.",
    mode: "influence",
    tags: ["페이싱", "리딩 분리"]
  },
  {
    title: "언어적 전제 (Linguistic Presuppositions)",
    group: "무의식 유도 및 심리 통제",
    original: "[주입하려는 생각]을 깨닫게 될 때 [바라는 행동]을 하게 됩니다.",
    ethical: "이 생각이 맞는지 먼저 검토해 보세요. 동의할 때만 다음 행동을 선택하면 됩니다.",
    principle: "전제를 숨기지 않고 상대가 전제 자체를 검토하게 한다.",
    mode: "influence",
    tags: ["전제", "검토권"]
  },
  {
    title: "양갈래 길 (Forking)",
    group: "무의식 유도 및 심리 통제",
    original: "동의하면 강화하고 부정하면 반대 해석으로 전환해 무조건 맞힙니다.",
    ethical: "동의하지 않으면 제 가설은 틀린 것으로 처리하고 새 질문으로 돌아가겠습니다.",
    principle: "어떤 답도 맞힌 것으로 만드는 구조를 버리고 피드백을 그대로 반영한다.",
    mode: "recovery",
    tags: ["포크", "피드백 반영"]
  }
];

function buildCard(seed: FormulaSeed): TechniqueCard {
  const config = modeConfig[seed.mode];
  const visualCue = visuals[config.visual];
  return {
    id: createId("card"),
    title: seed.title,
    formula: seed.original,
    manualFormula: seed.original,
    practiceFormula: seed.ethical,
    formulaGroup: seed.group,
    triggerSituations: [seed.group, "콜드리딩 훈련", "실전 커뮤니케이션", ...(seed.tags ?? [])],
    principle: seed.principle + " 매뉴얼 원형은 위험할 수 있으므로 앱에서는 동의, 확인, 보정이 있는 실행으로만 사용한다.",
    setup: config.setup,
    steps: config.steps,
    prompts: config.prompts,
    successCriteria: config.successCriteria,
    sentenceTemplates: [seed.ethical, ...config.sentenceTemplates.filter((line) => line !== seed.ethical)].slice(0, 3),
    exampleDialogues: [
      "상대: 요즘 좀 복잡해요. / 나: " + seed.ethical
    ],
    warnings: config.warnings,
    sourceNote: "콜드리딩 매뉴얼 / " + seed.group + " / 원형 공식을 안전 실행 공식으로 재구성",
    tags: ["콜드리딩", seed.group, ...(seed.tags ?? [])],
    visualCue
  };
}

export const coldReadingCards: TechniqueCard[] = formulaSeeds.map(buildCard);
